import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { defaultStorage, PENDING_KEY, isCancel } from "@/services/nativeCamera";
import { readDamageControlPhoto } from "@/services/damageControlPhoto";
import { canvasAt, jpegBlob } from "@/utils/imageToJpeg";

export const NATIVE_OPTIONS = {
  quality: 95,
  resultType: CameraResultType.Uri,
  source: CameraSource.Camera,
  correctOrientation: true,
  saveToGallery: false,
};
export function cameraAvailability({ platform, navigator: nav = globalThis.navigator } = {}) {
  const currentPlatform = platform || Capacitor.getPlatform();
  if (currentPlatform === "ios" || currentPlatform === "android") return { available: true, kind: "native" };
  const available = Boolean(nav && nav.mediaDevices && typeof nav.mediaDevices.getUserMedia === "function");
  return { available, kind: available ? "web" : null };
}

function withCapturedAt(blob, at) {
  Object.defineProperty(blob, "capturedAt", { value: at, enumerable: true });
  return blob;
}

export async function captureNative(deps = {}) {
  const camera = deps.camera || Camera;
  const fetchImpl = deps.fetchImpl || fetch;
  const now = deps.now || (() => new Date().toISOString());
  const storage = deps.storage || defaultStorage;
  const started = now();
  const pending = {
    kind: "damage_control",
    inspectionId: deps.inspectionId,
    zoneKey: deps.zoneKey,
    series: deps.series,
    attempt: deps.attempt,
    at: started instanceof Date ? started.toISOString() : new Date(started).toISOString(),
  };
  let photo;
  let capturedAt;
  await storage.set(PENDING_KEY, JSON.stringify(pending));
  try {
    try {
      photo = await camera.getPhoto(NATIVE_OPTIONS);
      const captured = now();
      capturedAt = captured instanceof Date ? captured.toISOString() : new Date(captured).toISOString();
    } catch (error) {
      if (isCancel(error)) return null;
      throw error;
    }
    if (!photo || !(photo.webPath || photo.path)) throw new Error("camera_photo_missing_path");
    const blob = await readDamageControlPhoto(photo, { ...deps, fetchImpl });
    return withCapturedAt(blob, capturedAt);
  } finally {
    await Promise.resolve(storage.remove(PENDING_KEY)).catch(() => {});
  }
}

export async function openWebStream(deps = {}) {
  const nav = deps.navigator || globalThis.navigator;
  if (!nav || !nav.mediaDevices || !nav.mediaDevices.getUserMedia) throw new Error("camera_unavailable");
  return nav.mediaDevices.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: "environment" }, width: { ideal: 4096 }, height: { ideal: 3072 } },
  });
}

export async function captureFrame(video, deps = {}) {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!(width > 0 && height > 0)) throw new Error("camera_frame_unavailable");
  // Capture time remains before reading the canvas context.
  let canvas;
  const capturedAt = new Date((deps.now || Date.now)()).toISOString();
  try {
    const target = canvasAt(width, height, deps, { error: "canvas_context", validate: false, releaseOnError: true });
    canvas = target.canvas;
    const context = target.context;
    context.drawImage(video, 0, 0, width, height);
    const blob = await jpegBlob(canvas, 0.95);
    return withCapturedAt(blob, capturedAt);
  } finally {
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}

export function stopStream(stream) {
  if (!stream || typeof stream.getTracks !== "function") return;
  stream.getTracks().forEach((track) => track.stop());
}
